import { useState, useCallback } from "react";
import { apiClient } from "../api/apiClient";

export interface PasskeyCredential {
  id: string;
  credentialId: string;
  displayName: string;
  createdAt: string;
  lastUsed?: string;
  authenticatorType: "platform" | "cross-platform";
}

export interface TOTPDevice {
  id: string;
  name: string;
  createdAt: string;
  lastUsed?: string;
  verified: boolean;
}

export interface QRCodeData {
  secret: string;
  qrCodeUrl: string;
  backupCodes: string[];
}

export const useMFA = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string>("");

  const arrayBufferToBase64url = useCallback((buffer: ArrayBuffer): string => {
    const bytes = new Uint8Array(buffer);
    const binary = Array.from(bytes)
      .map((b) => String.fromCharCode(b))
      .join("");
    return btoa(binary)
      .replace(/\+/g, "-")
      .replace(/\//g, "_")
      .replace(/=/g, "");
  }, []);

  const loadMFADevices = useCallback(async () => {
    setIsLoading(true);
    setError("");

    try {
      const [passkeyData, totpData] = await Promise.all([
        apiClient.get("/api/mfa/passkeys/"),
        apiClient.get("/api/mfa/totp-devices/"),
      ]);

      return {
        passkeys: passkeyData.data.passkeys || [],
        totpDevices: totpData.data.devices || [],
      };
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : "Failed to load MFA devices";
      setError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setIsLoading(false);
    }
  }, []);

  const verifyWithPasskey = useCallback(async (passkeys: PasskeyCredential[]) => {
    setIsLoading(true);
    setError("");

    try {
      if (!window.PublicKeyCredential) {
        throw new Error("WebAuthn is not supported in this browser");
      }

      const challengeResponse = await apiClient.post("/api/mfa/passkeys/challenge/");
      const { challenge, rpId } = challengeResponse.data;

      const getOptions: PublicKeyCredentialRequestOptions = {
        challenge: Uint8Array.from(
          atob(challenge.replace(/-/g, "+").replace(/_/g, "/")), 
          c => c.charCodeAt(0)
        ).buffer,
        rpId: rpId,
        timeout: 3000000,
        userVerification: "discouraged",
        allowCredentials: passkeys.map((pk) => ({
          type: "public-key" as const,
          id: Uint8Array.from(
            atob(pk.credentialId.replace(/-/g, "+").replace(/_/g, "/")),
            (c) => c.charCodeAt(0)
          ),
        })),
      };

      const assertion = await navigator.credentials.get({
        publicKey: getOptions,
      }) as PublicKeyCredential;

      if (!assertion) {
        throw new Error("Failed to get credential");
      }

      const response = assertion.response as AuthenticatorAssertionResponse;

      const verificationData = {
        credentialId: arrayBufferToBase64url(assertion.rawId),
        authenticatorData: arrayBufferToBase64url(response.authenticatorData),
        clientDataJSON: arrayBufferToBase64url(response.clientDataJSON),
        signature: arrayBufferToBase64url(response.signature),
        userHandle: response.userHandle ? arrayBufferToBase64url(response.userHandle) : null,
      };

      await apiClient.post("/api/mfa/passkeys/verify/", verificationData);
      return true;
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : "Failed to verify with passkey";
      setError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setIsLoading(false);
    }
  }, [arrayBufferToBase64url]);

  const verifyWithTOTP = useCallback(async (code: string) => {
    if (!code || code.length !== 6) {
      throw new Error("Please enter a valid 6-digit code");
    }

    setIsLoading(true);
    setError("");

    try {
      await apiClient.post("/api/mfa/totp/verify-login/", {
        code: code,
      });
      return true;
    } catch (error: any) {
      let errorMessage = "Failed to verify TOTP code";
      
      if (error.response?.data?.error) {
        errorMessage = error.response.data.error;
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      setError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setIsLoading(false);
    }
  }, []);

  const registerPasskey = useCallback(async (user: any, passkeys: PasskeyCredential[]) => {
    setIsLoading(true);
    setError("");

    try {
      if (!window.PublicKeyCredential) {
        throw new Error("WebAuthn is not supported in this browser");
      }

      const rpId = process.env.REACT_APP_RP_ID!;
      const appName = process.env.REACT_APP_NAME || "Your App";

      const isLocalhost =
        window.location.hostname === "localhost" ||
        window.location.hostname === "127.0.0.1" ||
        window.location.hostname === "::1";

      const userHandle = crypto.getRandomValues(new Uint8Array(32));
      const challenge = crypto.getRandomValues(new Uint8Array(32));

      const createOptions: PublicKeyCredentialCreationOptions = {
        challenge: challenge.buffer,
        rp: {
          name: appName,
          id: isLocalhost ? undefined : rpId,
        },
        user: {
          id: userHandle.buffer,
          name: user.email,
          displayName:
            `${user.first_name} ${user.last_name}`.trim() || user.name,
        },
        pubKeyCredParams: [
          { alg: -7, type: "public-key" }, // ES256
          { alg: -257, type: "public-key" }, // RS256
        ],
        timeout: 3000000,
        attestation: "none",
        authenticatorSelection: {
          userVerification: "discouraged",
          requireResidentKey: false,
          residentKey: "discouraged",
        },
        excludeCredentials: passkeys.map((pk) => ({
          type: "public-key" as const,
          id: Uint8Array.from(
            atob(pk.credentialId.replace(/-/g, "+").replace(/_/g, "/")),
            (c) => c.charCodeAt(0)
          ),
        })),
      };

      const credential = (await navigator.credentials.create({
        publicKey: createOptions,
      })) as PublicKeyCredential;

      if (!credential) {
        throw new Error("Failed to create credential");
      }

      const response = credential.response as AuthenticatorAttestationResponse;

      const credentialData = {
        credentialId: arrayBufferToBase64url(credential.rawId),
        publicKey: arrayBufferToBase64url(response.getPublicKey()!),
        userHandle: arrayBufferToBase64url(userHandle.buffer),
        username: user.email,
        displayName: `${user.first_name} ${user.last_name}`.trim() || user.name,
        authenticatorAttachment: "cross-platform",
      };

      const result = await apiClient.post("/api/mfa/add-passkey/", credentialData);
      return result.data.passkey;
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : "Failed to register passkey";
      setError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setIsLoading(false);
    }
  }, [arrayBufferToBase64url]);

  const setupTOTP = useCallback(async () => {
    setIsLoading(true);
    setError("");

    try {
      const result = await apiClient.post("/api/mfa/totp/setup/");
      return result.data;
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : "Failed to setup TOTP";
      setError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setIsLoading(false);
    }
  }, []);

  const verifyTOTPSetup = useCallback(async (secret: string, code: string, deviceName: string) => {
    setIsLoading(true);
    setError("");

    try {
      const result = await apiClient.post("/api/mfa/totp/verify-code/", {
        secret: secret,
        code: code,
        deviceName: deviceName || "Authenticator App",
      });

      return { success: true, verified: result.data.verified };
    } catch (error: any) {
      let errorMessage = "Failed to verify TOTP code";
      
      if (error.response?.data?.error) {
        errorMessage = error.response.data.error;
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      setError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setIsLoading(false);
    }
  }, []);

  const removePasskey = useCallback(async (passkeyId: string) => {
    try {
      await apiClient.delete(`/api/mfa/remove-passkey/${passkeyId}/`);
      return true;
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : "Failed to remove passkey";
      setError(errorMessage);
      throw new Error(errorMessage);
    }
  }, []);

  const removeTOTP = useCallback(async (deviceId: string) => {
    try {
      await apiClient.delete(`/api/mfa/totp-remove/${deviceId}/`);
      return true;
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : "Failed to remove TOTP device";
      setError(errorMessage);
      throw new Error(errorMessage);
    }
  }, []);

  const clearError = useCallback(() => {
    setError("");
  }, []);

  const hasMFAMethods = useCallback(async () => {
    try {
      const devices = await loadMFADevices();
      return devices.passkeys.length > 0 || devices.totpDevices.length > 0;
    } catch (error) {
      return false;
    }
  }, [loadMFADevices]);

  return {
    isLoading,
    error,
    clearError,
    loadMFADevices,
    verifyWithPasskey,
    verifyWithTOTP,
    registerPasskey,
    setupTOTP,
    verifyTOTPSetup,
    removePasskey,
    removeTOTP,
    hasMFAMethods,
  };
}; 